import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Check, X, ArrowUp, ArrowDown, Trash2, Loader2 } from 'lucide-react';
import { pbCategoriesApi } from '../api';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';

export const CategoryManagerDialog = ({ open, onOpenChange, onChanged }) => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    pbCategoriesApi.list()
      .then((r) => setCats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) { setNewName(''); setNewCode(''); setEditing(null); load(); } }, [open, load]);

  const fail = (e, msg) => {
    const d = e.response?.data?.detail;
    toast.error(typeof d === 'string' ? d : msg);
  };

  const changed = () => { load(); onChanged?.(); };

  const add = async () => {
    if (!newName.trim() || busy) return;
    setBusy(true);
    try {
      await pbCategoriesApi.create({ name: newName.trim(), code_prefix: newCode.trim() || null });
      toast.success('Category added');
      setNewName('');
      setNewCode('');
      changed();
    } catch (e) { fail(e, 'Could not add category'); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editName.trim() || busy) return;
    setBusy(true);
    try {
      await pbCategoriesApi.update(editing, { name: editName.trim(), code_prefix: editCode.trim() || null });
      toast.success('Category updated');
      setEditing(null);
      changed();
    } catch (e) { fail(e, 'Could not update category'); } finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    try {
      await pbCategoriesApi.delete(deleting.id);
      toast.success(`"${deleting.name}" deleted`);
      setDeleting(null);
      changed();
    } catch (e) { fail(e, 'Could not delete category'); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= cats.length) return;
    const reordered = [...cats];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setCats(reordered);
    try {
      await pbCategoriesApi.reorder(reordered.map((c) => c.id));
      onChanged?.();
    } catch (e) { fail(e, 'Could not reorder'); load(); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="category-manager-dialog">
        <DialogHeader>
          <DialogTitle>Manage Categories</DialogTitle>
          <DialogDescription className="text-gray-500">
            The Code auto-numbers new items in that category (e.g., FLR → FLR-001, FLR-002…). Renaming updates every item and assembly in it.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="New category name…" className={inputCls} data-testid="category-new-input" />
          <Input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="Code" maxLength={6} className={`${inputCls} w-24 shrink-0 uppercase`} data-testid="category-new-code" />
          <Button onClick={add} disabled={busy || !newName.trim()} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold shrink-0" data-testid="category-add-btn">
            <Plus size={15} /> Add
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-500"><Loader2 size={20} className="mx-auto animate-spin text-[#D4AF37]" /></div>
        ) : (
          <div className="space-y-1" data-testid="category-manager-list">
            {cats.map((c, idx) => (
              <div key={c.id} className="flex items-center gap-2 bg-[#161616] border border-gray-800 rounded-md px-3 py-2" data-testid={`category-row-${c.id}`}>
                {editing === c.id ? (
                  <>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveEdit()} className={`${inputCls} h-8`} autoFocus data-testid="category-rename-input" />
                    <Input value={editCode} onChange={(e) => setEditCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && saveEdit()} placeholder="Code" maxLength={6} className={`${inputCls} h-8 w-20 shrink-0 uppercase`} data-testid="category-code-input" />
                    <button onClick={saveEdit} className="text-green-400 hover:text-green-300 p-1" data-testid="category-rename-save"><Check size={16} /></button>
                    <button onClick={() => setEditing(null)} className="text-gray-500 hover:text-white p-1"><X size={16} /></button>
                  </>
                ) : (
                  <>
                    <span className="text-sm text-white flex-1 min-w-0 truncate">{c.name}</span>
                    {c.code_prefix && <span className="text-[10px] font-mono text-[#D4AF37] border border-[#D4AF37]/30 bg-[#D4AF37]/10 rounded px-1.5 py-0.5 shrink-0" data-testid={`category-code-${c.id}`}>{c.code_prefix}</span>}
                    <span className="text-[11px] text-gray-600 shrink-0">{c.item_count + c.assembly_count > 0 ? `${c.item_count} items · ${c.assembly_count} asm` : '—'}</span>
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-gray-500 hover:text-white p-1 disabled:opacity-30" title="Move up" data-testid={`category-up-${c.id}`}><ArrowUp size={14} /></button>
                    <button onClick={() => move(idx, 1)} disabled={idx === cats.length - 1} className="text-gray-500 hover:text-white p-1 disabled:opacity-30" title="Move down" data-testid={`category-down-${c.id}`}><ArrowDown size={14} /></button>
                    <button onClick={() => { setEditing(c.id); setEditName(c.name); setEditCode(c.code_prefix || ''); }} className="text-gray-500 hover:text-[#D4AF37] p-1" title="Edit name & code" data-testid={`category-rename-${c.id}`}><Pencil size={14} /></button>
                    <button onClick={() => setDeleting(c)} className="text-gray-500 hover:text-red-400 p-1" title="Delete" data-testid={`category-delete-${c.id}`}><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
          <AlertDialogContent className="bg-[#111111] border-gray-800 text-white" data-testid="category-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{deleting?.name}"?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                {deleting && (deleting.item_count + deleting.assembly_count) > 0
                  ? `${deleting.item_count} item(s) and ${deleting.assembly_count} assembl(ies) in this category will be moved to Uncategorized. `
                  : ''}
                The category is permanently deleted. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1A1A1A]">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="category-delete-confirm-btn">
                Delete Category
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
